import { useCallback } from 'react'
import { useDropzone, FileRejection } from 'react-dropzone'
import { Upload, FileX } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FileDropzoneProps {
  onFileSelect: (file: File) => void
  disabled?: boolean
  className?: string
}

/**
 * Accepted file types for upload.
 */
const ACCEPTED_TYPES = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/pdf': ['.pdf'],
}

export function FileDropzone({ onFileSelect, disabled, className }: FileDropzoneProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        // Could show toast here, but rejection state handles visual feedback
        console.warn('Rejected files:', rejectedFiles.map((r) => r.file.name))
      }
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0])
      }
    },
    [onFileSelect]
  )

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: ACCEPTED_TYPES,
    maxFiles: 1,
    disabled,
    onDrop,
  })

  return (
    <div
      {...getRootProps()}
      className={cn(
        'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all duration-200',
        'hover:border-primary hover:bg-primary/5',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isDragActive && !isDragReject && 'border-primary bg-primary/10 scale-[1.02]',
        isDragAccept && 'border-green-500 bg-green-50 dark:bg-green-950/20',
        isDragReject && 'border-destructive bg-destructive/10',
        disabled && 'opacity-50 cursor-not-allowed hover:border-border hover:bg-transparent',
        className
      )}
    >
      <input {...getInputProps()} />

      <div className="flex flex-col items-center gap-4">
        {isDragReject ? (
          <>
            <FileX className="h-12 w-12 text-destructive" />
            <div>
              <p className="text-lg font-semibold text-destructive">Invalid file type</p>
              <p className="text-sm text-muted-foreground mt-1">
                Please upload Excel (.xlsx, .xls) or PDF files only
              </p>
            </div>
          </>
        ) : isDragAccept ? (
          <>
            <Upload className="h-12 w-12 text-green-500 animate-bounce" />
            <div>
              <p className="text-lg font-semibold text-green-600">Drop to upload</p>
              <p className="text-sm text-muted-foreground mt-1">
                Release to start processing
              </p>
            </div>
          </>
        ) : (
          <>
            <Upload className="h-12 w-12 text-muted-foreground" />
            <div>
              <p className="text-lg font-semibold">
                {isDragActive ? 'Drop file here' : 'Drag & drop file here'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                or click to browse your files
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Supports: Excel (.xlsx, .xls) and PDF files
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
